import React, { useMemo } from 'react';
import { computeSevenDayTrend, RISK_TIERS, TIER_ORDER } from '../../../utils/riskTier';

const buildHistoryFromApi = (habRisk7Day) => {
  const { days, avg_risk_tier } = habRisk7Day;
  return days.map((dateStr, i) => {
    // avg_risk_tier is a per-day AVERAGE, so it's a float (e.g. 1.7, 2.3).
    // Round to the nearest whole tier before indexing — using the raw float as
    // an array index (TIER_ORDER[tier - 1]) returns undefined for any
    // non-integer value, which is why previously only all-baseline days (avg
    // exactly 1.0) rendered and every mixed/elevated day showed as blank.
    const tier = Math.round(avg_risk_tier[i]);
    const tierKey = (tier >= 1 && tier <= 4) ? TIER_ORDER[tier - 1] : null;
    const isLast = i === days.length - 1;
    const day = isLast
      ? 'Today'
      : new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
    return { day, tierKey };
  });
};

export default function SevenDayTrend({ habRisk7Day, loading, transformedData, currentTierKey }) {
  const history = useMemo(() => {
    if (habRisk7Day?.days?.length && habRisk7Day?.avg_risk_tier?.length) {
      return buildHistoryFromApi(habRisk7Day);
    }
    return computeSevenDayTrend(transformedData);
  }, [habRisk7Day, transformedData]);

  const consecutiveDays = useMemo(() => {
    let count = 0;
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].tierKey === currentTierKey) count++;
      else break;
    }
    return count;
  }, [history, currentTierKey]);

  const tc = RISK_TIERS[currentTierKey] || RISK_TIERS.BASELINE;

  const sectionLabel = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.07em',
    textTransform: 'uppercase',
    color: '#6f6a5f',
    margin: '0 0 14px',
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ece7db',
      borderRadius: 16,
      padding: '22px 24px',
      boxShadow: '0 1px 2px rgba(31,42,46,0.04)',
      marginBottom: 16,
    }}>
      <p style={sectionLabel}>7-day risk trend</p>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            border: '3px solid #e2e8f0', borderTopColor: '#46708a',
            animation: 'spin 0.8s linear infinite',
          }} />
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10 }}>
        {history.map((h, i) => {
          const tierKey = h.tierKey || 'BASELINE';
          const rk = RISK_TIERS[tierKey] || RISK_TIERS.BASELINE;
          const today = i === history.length - 1;
          return (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: rk.color, marginBottom: 6 }}>
                {h.tierKey ? rk.label : '—'}
              </div>
              <div style={{
                height: 40,
                borderRadius: 9,
                background: h.tierKey ? (today ? rk.solid : rk.bg) : '#f1ede3',
                border: `1px solid ${h.tierKey ? rk.border : '#e3ddcf'}`,
              }} />
              <div style={{
                fontSize: 13,
                color: today ? '#1f2a2e' : '#6f6a5f',
                fontWeight: today ? 700 : 500,
                marginTop: 6,
              }}>
                {h.day}
              </div>
            </div>
          );
        })}
      </div>
      )}
      <p style={{ fontSize: 15, color: '#46505a', marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1ede3', lineHeight: 1.6 }}>
        {consecutiveDays >= 2 ? `Day ${consecutiveDays} at ${tc.label} level. ` : ''}
        Based on continuous sensor readings, environmental conditions, and community reports.
      </p>
    </div>
  );
}
